import { inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { CrmAiAccessService } from '../core/crm-ai-access.service';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AccountGroup, AccountGroupService } from '../account-group';
import { editionIds, partnerId, productIds } from '../../globals';
import { iolRestricted$, iolRestrictedSeat$ } from '../iol-restriction';
import {
  AI_DEFAULT_BUSINESS_ASSISTANT_WORKFORCE,
  AiAssistantConfig,
  AiConnection,
  ASSISTANT_ID_AI_SEARCH_EXPERT,
  ASSISTANT_ID_CHAT_RECEPTIONIST,
  AssistantFieldLockMap,
  ConnectionType,
  CUSTOM_ASSISTANT_PLACEHOLDER_ID,
  MY_LISTING_CONNECTION_NAME,
  NamespaceConfig,
  PlatformLocation,
  AssistantProductMap,
  AssistantTypeProductMap,
  CapabilityProductMap,
  convertAssistantTypeToDefaultID,
} from '@galaxy/ai-assistant';
import { AppPartnerService } from '../core/app_partner.service';
import { AppKey, App, AppPartnerService as MarketplaceAppPartnerService } from '@galaxy/marketplace-apps';
import { ProductService } from '../core/product.service';
import { ProductUpgradeService } from '../core/product-upgrade.service';
import { PartnerService, Product } from '../core';
import {
  AI_DEFAULT_BUSINESS_ASSISTANT_CONNECTIONS,
  ASSISTANT_PRODUCT_DESCRIPTORS,
  CAPABILITY_PRODUCT_MAP,
  REPUTATION_CAPABILITY_IDS,
  businessCenterAssetPath,
} from './ai-assistant-constants';
import { getConfiguration$ } from '@vendasta/protos/listing_products/v1/partner-settings-service';
import type { Configuration } from '@vendasta/protos/listing_products/v1/types';
import { ListingProfileService } from '@galaxy/listing-profile-common';
import { isBrand, LocationsService } from '../locations';
import { AssistantType, AssistantTypeStatusStatus } from '@vendasta/protos/ai_assistants/v1alpha1';
import { getAssistantTypeStatus$ } from '@vendasta/protos/ai_assistants/v1alpha1/assistant-service';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '../auth.service';
import { FeatureFlagService } from '../core/feature-flag.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

// A partner-hidden type is absent from statuses entirely; an unentitled one is absent or DISABLED.
// Either way the account cannot have a custom AI employee, so both the workforce card and the
// Create button read this one predicate rather than deciding separately. Employees created while
// the type was still available go with it — the config page, the Inbox chat picker and the select
// dialog all follow this flag. That is intended for both states: a partner who hides the type has
// decided not to offer it, and the workforce page already dropped them once the entitlement lapsed.
function isCustomAssistantEnabled(response: {
  statuses: readonly { type: AssistantType; status: AssistantTypeStatusStatus }[];
}): boolean {
  return response.statuses.some(
    (s) => s.type === AssistantType.ASSISTANT_TYPE_CUSTOM && s.status === AssistantTypeStatusStatus.STATUS_ENABLED,
  );
}

export const AI_ASSISTANTS_CONFIG = new InjectionToken<AiAssistantConfig>(
  '[Business Center Client]: Token for AI assistants config',
  {
    providedIn: 'root',
    factory: function (): AiAssistantConfig {
      const accountGroupService = inject(AccountGroupService);
      const appPartnerService = inject(AppPartnerService);
      const marketplaceAppPartnerService = inject(MarketplaceAppPartnerService);
      const productService = inject(ProductService);
      const productUpgradeService = inject(ProductUpgradeService);
      const partnerService = inject(PartnerService);
      const locationService = inject(LocationsService);
      const authService = inject(AuthService);
      const featureFlagService = inject(FeatureFlagService);
      const crmAiAccessService = inject(CrmAiAccessService);
      const snackbarService = inject(SnackbarService);
      const listingProfileService = inject(ListingProfileService);
      const router = inject(Router);

      const currentLocation$ = locationService.currentLocation$.pipe(
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const accountGroupId$ = currentLocation$.pipe(
        filter((loc) => loc !== null),
        distinctUntilChanged(),
        map((location) => {
          return isBrand(location) ? '' : (<AccountGroup>location).accountGroupId;
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const primaryOperatingLanguage$ = accountGroupId$.pipe(
        switchMap((accountGroupId) =>
          accountGroupId
            ? listingProfileService.get(accountGroupId, { napData: true }).pipe(
                map((profile) => profile?.napData?.primaryOperatingLanguage || ''),
                catchError(() => of('')),
              )
            : of(''),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const marketId$ = currentLocation$.pipe(
        filter((loc) => loc !== null),
        map((location) => location.marketId),
        filter<string>(Boolean),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const activeProducts$ = accountGroupId$.pipe(
        switchMap((agId) => (agId ? productService.activeProducts$ : of([] as Product[]))),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const partnerId$ = of(partnerId);

      const msChatWidgetEnabled$ = partnerService.wlConfig$.pipe(map((config) => config.msChatWidgetEnabled));

      const config$ = combineLatest([msChatWidgetEnabled$, partnerId$, accountGroupId$]).pipe(
        switchMap(([enabled, partnerId, accountGroupId]) => {
          if (enabled && accountGroupId) {
            return getConfiguration$({ partnerId: partnerId, businessId: accountGroupId }).pipe(
              map((resp) => resp?.configuration),
              catchError(() => {
                console.error('Error getting partner configuration');
                return of({} as Configuration);
              }),
            );
          }
          return of({} as Configuration);
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      // ai-assistants already subtracts the assistant types the partner hid for this market, so
      // every consumer of this stream is filtered without asking for it. Hiding is not
      // display-only there — a hidden type is also refused by the listing and answer paths — and
      // the backend fails the read rather than degrading to "nothing hidden", which is why the
      // catchError below falls back to an empty workforce instead of an unfiltered one.
      const assistantTypeStatus$ = accountGroupId$.pipe(
        switchMap((accountGroupId) =>
          accountGroupId
            ? getAssistantTypeStatus$({ namespace: { accountGroupNamespace: { accountGroupId } } }).pipe(
                catchError((err) => {
                  console.error('Error fetching assistant type status', err);
                  snackbarService.openErrorSnack('AI_ASSISTANT.WORKFORCE.ERRORS.LOAD_FAILED');
                  return of({ statuses: [], canManageAssistants: false });
                }),
              )
            : of({ statuses: [], canManageAssistants: false }),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const voiceAIAvailable$ = assistantTypeStatus$.pipe(
        map((response) => response.statuses.some((s) => s.type === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const aiMarketerAvailable$ = assistantTypeStatus$.pipe(
        map((response) => response.statuses.some((s) => s.type === AssistantType.ASSISTANT_TYPE_MARKETER)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      // canManageAssistants answers "may this user manage assistants here", not "may this account
      // have a custom one" — ai-assistants derives it from a deliberately non-hidden assistant, so
      // it stays true after a partner hides the Custom type. Gate on the type status as well, using
      // the same predicate the workforce list uses, so the Create button cannot outlive the card it
      // creates.
      const showCustomAssistant$ = assistantTypeStatus$.pipe(
        map((response) => response.canManageAssistants && isCustomAssistantEnabled(response)),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const soldAppKeys = [
        new AppKey({ appId: productIds.conversationAI }),
        new AppKey({ appId: productIds.reputationManagement }),
        new AppKey({ appId: productIds.listingBuilder, editionId: editionIds.listingBuilderPro }),
        new AppKey({ appId: productIds.socialMarketing }),
      ];

      const currentAccountGroupOrNull$: Observable<AccountGroup | null> = accountGroupId$.pipe(
        switchMap((accountGroupId) => (accountGroupId ? accountGroupService.currentAccountGroup$ : of(null))),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const soldApps$: Observable<App[] | null> = currentAccountGroupOrNull$.pipe(
        switchMap((ag) =>
          ag
            ? appPartnerService.getMulti(soldAppKeys, ag.marketId).pipe(
                catchError((err) => {
                  console.error('Error fetching sold marketplace apps', err);
                  return of(null);
                }),
              )
            : of(null),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const reputationIconUrl = businessCenterAssetPath('reputation-management-logo.png');
      const socialMarketingIconUrl = businessCenterAssetPath('social-ai-icon.png');

      const assistantProductMap$ = soldApps$.pipe(
        map((apps) => {
          const appById = new Map((apps ?? []).map((app) => [app.key?.appId, app] as const));
          const conversationAIApp = appById.get(productIds.conversationAI);
          const reputationApp = appById.get(productIds.reputationManagement);
          const localSEOApp = appById.get(productIds.listingBuilder);
          const socialMarketingApp = appById.get(productIds.socialMarketing);

          const assistantTypeMap: AssistantTypeProductMap = {};
          const capabilityIdMap: CapabilityProductMap = {};

          if (conversationAIApp?.sharedMarketingInformation?.iconUrl) {
            const iconUrl = conversationAIApp.sharedMarketingInformation.iconUrl;
            const name = conversationAIApp.sharedMarketingInformation.name || 'Conversations AI';
            const editionName = conversationAIApp.sharedMarketingInformation.editionName;

            assistantTypeMap[AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST] = {
              iconUrl: iconUrl,
              productName: editionName ? `${name} ${editionName}` : `${name} Premium`,
            };
            assistantTypeMap[AssistantType.ASSISTANT_TYPE_INBOX] = {
              iconUrl: iconUrl,
              productName: editionName ? `${name} ${editionName}` : `${name} Standard`,
            };
          }

          if (reputationApp?.sharedMarketingInformation) {
            const reputationName = reputationApp.sharedMarketingInformation.name || 'Reputation AI';

            REPUTATION_CAPABILITY_IDS.forEach((capabilityId) => {
              capabilityIdMap[capabilityId] = {
                iconUrl: reputationIconUrl,
                productName: reputationName,
              };
            });
          }

          if (localSEOApp?.sharedMarketingInformation?.iconUrl) {
            const localSEOName = localSEOApp.sharedMarketingInformation.name || 'Local SEO';
            const editionName = localSEOApp.sharedMarketingInformation.editionName;
            assistantTypeMap[AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT] = {
              iconUrl: localSEOApp.sharedMarketingInformation.iconUrl,
              productName: editionName ? `${localSEOName} ${editionName}` : localSEOName,
            };
          }

          {
            const socialMarketingName = socialMarketingApp?.sharedMarketingInformation?.name || 'Social AI';
            const socialMarketingIcon =
              socialMarketingApp?.sharedMarketingInformation?.iconUrl || socialMarketingIconUrl;
            assistantTypeMap[AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING] = {
              iconUrl: socialMarketingIcon,
              productName: socialMarketingName,
            };
            assistantTypeMap[AssistantType.ASSISTANT_TYPE_BLOGGER] = {
              iconUrl: socialMarketingIcon,
              productName: socialMarketingName,
            };
          }

          return { assistantTypeMap, capabilityIdMap } as AssistantProductMap;
        }),
        catchError((err) => {
          console.error('Error fetching assistant product map:', err);
          return of({ assistantTypeMap: {}, capabilityIdMap: {} } as AssistantProductMap);
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const descriptorAppIds = Array.from(
        new Set(Object.values(ASSISTANT_PRODUCT_DESCRIPTORS).map((descriptor) => descriptor.appId)),
      );

      const hiddenUpgradeAppIds$: Observable<Set<string>> = currentAccountGroupOrNull$.pipe(
        switchMap((ag) =>
          ag
            ? forkJoin(
                descriptorAppIds.map((appId) =>
                  marketplaceAppPartnerService.getAppSettings(appId, partnerId, ag.marketId).pipe(
                    map((settings) => (settings?.editionChange?.hideUpgradeCta === true ? appId : null)),
                    catchError(() => of(null)),
                  ),
                ),
              ).pipe(map((ids) => new Set(ids.filter((id): id is string => !!id))))
            : of(new Set<string>()),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const defaultAIWorkforce$ = combineLatest([
        assistantTypeStatus$,
        config$,
        hiddenUpgradeAppIds$,
        assistantProductMap$,
      ]).pipe(
        map(([response, configuration, hiddenUpgradeAppIds, assistantProductMap]) => {
          const statusByDefaultId = new Map<string, AssistantTypeStatusStatus>(
            response.statuses.map((s) => [convertAssistantTypeToDefaultID(s.type), s.status] as const),
          );
          const localSEOUrl = configuration?.publicMyListingUrl || '';

          return AI_DEFAULT_BUSINESS_ASSISTANT_WORKFORCE.filter((assistant) => {
            if (assistant.assistant.id === CUSTOM_ASSISTANT_PLACEHOLDER_ID) {
              return isCustomAssistantEnabled(response);
            }
            if (!statusByDefaultId.has(assistant.assistant.id)) {
              return false;
            }
            const status = statusByDefaultId.get(assistant.assistant.id);
            if (status !== AssistantTypeStatusStatus.STATUS_DISABLED) {
              return true;
            }
            return !!ASSISTANT_PRODUCT_DESCRIPTORS[assistant.assistant.id];
          }).map((assistant) => {
            if (assistant.assistant.id === CUSTOM_ASSISTANT_PLACEHOLDER_ID) {
              return assistant;
            }
            const status = statusByDefaultId.get(assistant.assistant.id);
            const isDisabled = status === AssistantTypeStatusStatus.STATUS_DISABLED;
            const isComingSoon = status === AssistantTypeStatusStatus.STATUS_COMING_SOON;
            const hydrated = {
              ...assistant,
              isDisabled,
              isComingSoon,
            };

            if (assistant.assistant.id === ASSISTANT_ID_CHAT_RECEPTIONIST && !isDisabled && localSEOUrl) {
              hydrated.cta = {
                label: 'AI_ASSISTANT.WORKFORCE.TRY_IT',
                action: {
                  showButton: true,
                  newTab: true,
                  url: localSEOUrl,
                },
              };
            }

            const aiSearchProductIcon =
              assistantProductMap.assistantTypeMap?.[AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT];
            if (assistant.assistant.id === ASSISTANT_ID_AI_SEARCH_EXPERT && aiSearchProductIcon) {
              hydrated.productIcons = [aiSearchProductIcon];
            }

            const descriptor = ASSISTANT_PRODUCT_DESCRIPTORS[assistant.assistant.id];
            // No CTA when hidden: the card keeps its non-clickable locked badge so the employee still reads as inactive.
            if (isDisabled && descriptor && !hiddenUpgradeAppIds.has(descriptor.appId)) {
              hydrated.cta = {
                label: 'AI_ASSISTANT.SHARED.UPGRADE',
                action: {
                  showButton: true,
                  callback: () =>
                    productUpgradeService.openUpgradeDialog({
                      appId: descriptor.appId,
                      targetEditionName: descriptor.targetEditionName,
                    }),
                },
              };
            }
            return hydrated;
          });
        }),
      );

      const namespaceConfig$: Observable<NamespaceConfig> = accountGroupId$.pipe(
        filter(Boolean),
        map((accountGroupId) => {
          return {
            namespace: {
              accountGroupNamespace: {
                accountGroupId: accountGroupId,
              },
            },
            root: `/account/location/${accountGroupId}/ai`,
          };
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const defaultConnections$ = combineLatest([accountGroupId$, activeProducts$, config$]).pipe(
        map(([accountGroupId, activeProducts, config]) =>
          buildAIDefaultConnections(accountGroupId, activeProducts, config),
        ),
      );

      const currentUserId$ = toObservable(authService.effectiveUserId);

      // For a restricted IOL seat, Role/Model/Capabilities/Tools are always locked regardless of edition,
      // while Voice/Knowledge only lock without the Premium edition. Everyone else edits freely.
      const assistantFieldLocks$: Observable<AssistantFieldLockMap> = combineLatest([
        iolRestrictedSeat$(),
        iolRestricted$(activeProducts$),
      ]).pipe(
        map(([seatRestricted, restrictedWithoutPremium]) => ({
          prompt: seatRestricted,
          model: seatRestricted,
          capabilities: seatRestricted,
          tools: seatRestricted,
          voice: restrictedWithoutPremium,
          knowledge: restrictedWithoutPremium,
        })),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const featureFlags$ = combineLatest([partnerId$, marketId$]).pipe(
        distinctUntilChanged(),
        switchMap(([partnerId, marketId]) =>
          featureFlagService.checkFeatureFlagsMulti(partnerId, marketId, [
            'sales_coach_capability_more_entities_config',
          ]),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const crmUpdateMoreEntitiesEnabled$ = featureFlags$.pipe(
        map((flags) => flags['sales_coach_capability_more_entities_config']),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const navigateToOperatingLanguage = (): void => {
        accountGroupId$.pipe(take(1)).subscribe((accountGroupId) => {
          if (!accountGroupId) {
            return;
          }
          router.navigate(['/account/location', accountGroupId, 'settings', 'profile'], {
            fragment: 'primary-operating-language',
          });
        });
      };

      return {
        accountGroupId$,
        marketId$,
        partnerId$,
        defaultConnections$,
        defaultAIWorkforce$,
        namespaceConfig$,
        currentUserId$,
        voiceAIAvailable$,
        showCustomAssistant$,
        platformLocation: PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP,
        aiMarketerAvailable$,
        personalEmailAiDraftingAvailable$: crmAiAccessService.crmAiFeatures$.pipe(
          map((features) => features.aiDraftReply),
        ),
        capabilityProductMap: CAPABILITY_PRODUCT_MAP,
        assistantProductMap$: assistantProductMap$,
        crmUpdateMoreEntitiesEnabled$,
        primaryOperatingLanguage$,
        navigateToOperatingLanguage,
        assistantFieldLocks$,
        isImpersonating$: toObservable(authService.isImpersonating),
      };
    },
  },
);

function buildAIDefaultConnections(
  accountGroupId: string,
  activeProducts: Product[],
  config: Configuration,
): AiConnection[] {
  const defaultConnections = AI_DEFAULT_BUSINESS_ASSISTANT_CONNECTIONS.map((aiConnection) => {
    return {
      ...aiConnection,
      connection: {
        ...aiConnection.connection,
        namespace: { accountGroupNamespace: { accountGroupId: accountGroupId } },
      },
    };
  });

  const aiDefaultConnections: AiConnection[] = [];
  defaultConnections.forEach((defaultAiConnection) => {
    const aiConnection = structuredClone(defaultAiConnection);
    const connection = aiConnection.connection;

    if (connection.connectionType === ConnectionType.AutoReviewResponse) {
      const reputationProduct = activeProducts?.find(
        (product) => product.productId === productIds.reputationManagement,
      );
      if (reputationProduct?.iconUrl) {
        aiConnection.connection.iconUrl = reputationProduct.iconUrl;
        aiDefaultConnections.push(aiConnection);
      }
    }
    if (connection.connectionType === ConnectionType.WebchatWidget) {
      if (connection.name === MY_LISTING_CONNECTION_NAME && config?.publicMyListingUrl && !config?.chatWidgetId) {
        aiConnection.cta = {
          label: 'AI_ASSISTANT.SETTINGS.CONNECTIONS.TRY_MY_LISTING',
          action: {
            showButton: true,
            url: config.publicMyListingUrl,
            newTab: true,
          },
        };
        aiDefaultConnections.push(aiConnection);
      }
    }
  });

  return aiDefaultConnections;
}
